import React, { useEffect, useState } from 'react';
import { MegaphoneIcon, ArrowLeftIcon, EditIcon, TrashIcon, InfoIcon, CalendarIcon, TagIcon, DollarSignIcon, PercentIcon, UserIcon, BuildingIcon, CreditCardIcon, BarChart2Icon, LineChartIcon, TrendingUpIcon, PieChartIcon, DownloadIcon, XIcon, CheckIcon } from 'lucide-react';
import { Campaign } from '../types/Campaign';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { ConversionFunnelChart, ConversionRatePieChart, CodeUsageTimeSeriesChart, PaymentVolumeTimeSeriesChart } from '../components/campaigns/CampaignCharts';
// Mock campaign data - would normally be fetched from API
const mockCampaigns: Record<string, Campaign> = {
  RBLSHOP2025: {
    id: 'RBLSHOP2025',
    title: '[RLife] 7-days Promotion for Shop',
    description: '[RLife] 7-days Promotion for Shop',
    expiryDate: '07/10/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: 'LKR',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'RBLSHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    companies: ['Rabbit Life'],
    transactionVolume: 0,
    transactionCount: 0
  },
  BBL10THB: {
    id: 'BBL10THB',
    title: 'BBL Special Package (Rs. 10 Discount)',
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/08/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: 'LKR',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BBL10THB',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  }
};
// Tab interface for analytics section
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}
export function CampaignDetails() {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('conversion');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState('30d');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const analyticsTabs: AnalyticsTab[] = [{
    id: 'conversion',
    label: 'Conversion',
    icon: <BarChart2Icon size={16} data-id="element-4556" />
  }, {
    id: 'usage',
    label: 'Code Usage',
    icon: <LineChartIcon size={16} data-id="element-4557" />
  }, {
    id: 'volume',
    label: 'Payment Volume',
    icon: <TrendingUpIcon size={16} data-id="element-4558" />
  }];
  useEffect(() => {
    // In a real app, we would fetch the campaign data from an API
    // For now, we'll use the URL parameter to get the campaign ID
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    window.location.href = '/campaigns';
  };
  const navigateToEdit = () => {
    if (campaign) {
      window.location.href = `/campaign-form?id=${campaign.id}`;
    }
  };
  const handleExport = () => {
    setExportStatus('loading');
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
      // Reset after showing success message
      setTimeout(() => {
        setExportStatus('idle');
        setShowExportModal(false);
      }, 2000);
    }, 1000);
  };
  // Calculate budget usage percentage
  const calculateBudgetUsage = () => {
    if (!campaign || campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-4559">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-id="element-4560"></div>
      </div>;
  }
  if (!campaign) {
    return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-4561">
        <h2 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-4562">
          Campaign not found
        </h2>
        <p className="text-gray-600 mb-4" data-id="element-4563">
          The campaign you are looking for does not exist or has been removed.
        </p>
        <button onClick={navigateBack} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4564">
          Back to Campaigns
        </button>
      </div>;
  }
  // Render analytics content based on active tab
  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 'conversion':
        return <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4565">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-4566">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-4567">
                Conversion Funnel
              </h3>
              <ConversionFunnelChart data-id="element-4568" />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-4569">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-4570">
                Conversion Rates
              </h3>
              <ConversionRatePieChart data-id="element-4571" />
            </div>
          </div>;
      case 'usage':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-4572">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-4573">
              Code Usage Over Time (Last 30 Days)
            </h3>
            <CodeUsageTimeSeriesChart data-id="element-4574" />
          </div>;
      case 'volume':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-4575">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-4576">
              Payment Volume Over Time (Last 30 Days)
            </h3>
            <PaymentVolumeTimeSeriesChart currency={campaign.currency} data-id="element-4577" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-4578">
      {/* Top bar with actions */}
      <div className="flex justify-between items-center" data-id="element-4579">
        <button onClick={navigateBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-4580">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-4581" />
          <span data-id="element-4582">Back to Campaigns</span>
        </button>
        <div className="flex space-x-3" data-id="element-4583">
          <button onClick={() => setShowExportModal(true)} className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-4584">
            <DownloadIcon size={16} className="mr-2" data-id="element-4585" />
            Export CSV
          </button>
          <button onClick={navigateToEdit} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-4586">
            <EditIcon size={16} className="mr-2" data-id="element-4587" />
            Edit Campaign
          </button>
          <button className="px-3 py-2 bg-white border border-red-500 text-red-500 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-4588">
            <TrashIcon size={16} className="mr-2" data-id="element-4589" />
            Delete
          </button>
        </div>
      </div>
      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-4590">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" data-id="element-4591">
            <div className="flex justify-between items-center mb-4" data-id="element-4592">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-4593">
                Export Campaign Data
              </h3>
              <button onClick={() => {
            setShowExportModal(false);
            setExportStatus('idle');
          }} className="text-gray-400 hover:text-gray-500" data-id="element-4594">
                <XIcon size={20} data-id="element-4595" />
              </button>
            </div>
            <div className="mb-4" data-id="element-4596">
              <p className="text-sm text-gray-600 mb-2" data-id="element-4597">
                Select time period for export:
              </p>
              <TimeSelector value={exportTimeframe} onChange={setExportTimeframe} data-id="element-4598" />
            </div>
            <div className="mb-4" data-id="element-4599">
              <p className="text-sm text-gray-600" data-id="element-4600">
                The export will include all campaign data for the selected
                period:
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-gray-600" data-id="element-4601">
                <li data-id="element-4602">Daily code usage statistics</li>
                <li data-id="element-4603">Conversion metrics</li>
                <li data-id="element-4604">Transaction volumes and amounts</li>
                <li data-id="element-4605">User engagement data</li>
              </ul>
            </div>
            <div className="flex justify-end" data-id="element-4606">
              {exportStatus === 'idle' && <button onClick={handleExport} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-4607">
                  <DownloadIcon size={16} className="mr-2" data-id="element-4608" />
                  Export
                </button>}
              {exportStatus === 'loading' && <button disabled className="px-4 py-2 bg-blue-600 text-white rounded-md opacity-70 flex items-center" data-id="element-4609">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" data-id="element-4610"></div>
                  Exporting...
                </button>}
              {exportStatus === 'success' && <button disabled className="px-4 py-2 bg-green-600 text-white rounded-md flex items-center" data-id="element-4611">
                  <CheckIcon size={16} className="mr-2" data-id="element-4612" />
                  Export Complete
                </button>}
              {exportStatus === 'error' && <button onClick={handleExport} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-4613">
                  Try Again
                </button>}
            </div>
          </div>
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-4614">
        <div className="flex items-center mb-6" data-id="element-4615">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-4616">
            <MegaphoneIcon size={24} className="text-blue-600" data-id="element-4617" />
          </div>
          <div data-id="element-4618">
            <h1 className="text-2xl font-bold text-gray-800" data-id="element-4619">
              {campaign.title}
            </h1>
            <p className="text-gray-500" data-id="element-4620">Campaign ID: {campaign.id}</p>
          </div>
          <div className="ml-auto" data-id="element-4621">
            <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' : campaign.status === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-4622">
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
          </div>
        </div>
        {/* Campaign statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-4623">
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-4624">
            <div className="flex items-center justify-between mb-4" data-id="element-4625">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-4626">
                Total Budget
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-4627" />
            </div>
            <div className="flex items-center mb-2" data-id="element-4628">
              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-4629">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                width: `${calculateBudgetUsage()}%`
              }} data-id="element-4630"></div>
              </div>
              <span className="text-sm text-gray-500" data-id="element-4631">
                {calculateBudgetUsage()}%
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-4632">
              {campaign.budgetUsed.toLocaleString('en-LK')} /{' '}
              {campaign.budget.toLocaleString('en-LK')} Rs.
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-4633">
            <div className="flex items-center justify-between mb-4" data-id="element-4634">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-4635">
                Transaction volume for campaign
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-4636" />
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-4637">
              Rs.{' '}
              {campaign.transactionVolume?.toLocaleString('en-LK') || '0.00'}
            </div>
            <div className="text-xs text-gray-500 mt-1" data-id="element-4638">
              {campaign.transactionCount || 0} transactions have been collected
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-4639">
            <div className="flex items-center justify-between mb-4" data-id="element-4640">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-4641">KPI</h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-4642" />
            </div>
            <div className="space-y-4" data-id="element-4643">
              <div data-id="element-4644">
                <p className="text-xs text-gray-500" data-id="element-4645">Transaction volume ROI</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-4646">0</p>
                <p className="text-xs text-gray-500" data-id="element-4647">
                  Against a budget of Rs. 0.00
                </p>
              </div>
              <div data-id="element-4648">
                <p className="text-xs text-gray-500" data-id="element-4649">Transactions</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-4650">0</p>
                <p className="text-xs text-gray-500" data-id="element-4651">0 Merchants</p>
              </div>
            </div>
          </div>
        </div>
        {/* Campaign Analytics Section */}
        <div className="border-t border-gray-200 pt-6 mb-8" data-id="element-4652">
          <div className="flex justify-between items-center mb-6" data-id="element-4653">
            <h2 className="text-lg font-medium text-gray-800 flex items-center" data-id="element-4654">
              <PieChartIcon size={20} className="mr-2 text-blue-600" data-id="element-4655" />
              Campaign Analytics
            </h2>
          </div>
          {/* Analytics Tabs */}
          <div className="mb-6" data-id="element-4656">
            <div className="border-b border-gray-200" data-id="element-4657">
              <nav className="flex -mb-px space-x-8" data-id="element-4658">
                {analyticsTabs.map(tab => <button key={tab.id} className={`py-4 px-1 flex items-center text-sm font-medium ${activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab(tab.id)} data-id="element-4659">
                    <span className="mr-2" data-id="element-4660">{tab.icon}</span>
                    {tab.label}
                  </button>)}
              </nav>
            </div>
          </div>
          {/* Analytics Content */}
          {renderAnalyticsContent()}
        </div>
        {/* Campaign details */}
        <div className="border-t border-gray-200 pt-6" data-id="element-4661">
          <div className="flex justify-between items-center mb-4" data-id="element-4662">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-4663">
              Campaign details
            </h2>
            <button onClick={navigateToEdit} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-4664">
              <EditIcon size={14} className="mr-1" data-id="element-4665" />
              Edit details
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-id="element-4666">
            <div data-id="element-4667">
              <h3 className="text-sm text-gray-500" data-id="element-4668">Title</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4669">
                {campaign.title}
              </p>
            </div>
            <div data-id="element-4670">
              <h3 className="text-sm text-gray-500" data-id="element-4671">Category</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4672">
                {campaign.category}
              </p>
            </div>
            <div data-id="element-4673">
              <h3 className="text-sm text-gray-500" data-id="element-4674">Conditions</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4675">
                {campaign.conditions || 'No information'}
              </p>
            </div>
            <div data-id="element-4676">
              <h3 className="text-sm text-gray-500" data-id="element-4677">Expiry Date</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4678">
                {campaign.expiryDate}
              </p>
            </div>
            <div data-id="element-4679">
              <h3 className="text-sm text-gray-500" data-id="element-4680">Description</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4681">
                {campaign.description}
              </p>
            </div>
            <div data-id="element-4682">
              <h3 className="text-sm text-gray-500" data-id="element-4683">Campaign Owner</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4684">
                {campaign.owner}
              </p>
            </div>
            <div data-id="element-4685">
              <h3 className="text-sm text-gray-500" data-id="element-4686">Code Strategy</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4687">
                {campaign.codeStrategy}
              </p>
            </div>
            <div data-id="element-4688">
              <h3 className="text-sm text-gray-500" data-id="element-4689">Min. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4690">
                Rs.{' '}
                {campaign.minTransactionValue.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-4691">
              <h3 className="text-sm text-gray-500" data-id="element-4692">Max. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4693">
                Rs. {campaign.maxTransactionValue.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-4694">
              <h3 className="text-sm text-gray-500" data-id="element-4695">Min. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4696">
                Rs.{' '}
                {campaign.minDiscount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-4697">
              <h3 className="text-sm text-gray-500" data-id="element-4698">Max. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4699">
                Rs.{' '}
                {campaign.maxDiscount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-4700">
              <h3 className="text-sm text-gray-500" data-id="element-4701">Valid MCC</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4702">
                {campaign.validMCC?.join(', ') || 'All MCCs'}
              </p>
            </div>
            <div data-id="element-4703">
              <h3 className="text-sm text-gray-500" data-id="element-4704">Valid on days</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4705">
                {campaign.validDays.length === 7 ? 'All days' : campaign.validDays.join(', ')}
              </p>
            </div>
            <div data-id="element-4706">
              <h3 className="text-sm text-gray-500" data-id="element-4707">Buyer Identifier</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4708">
                {campaign.validIdentities?.join(', ') || 'No information'}
              </p>
            </div>
            <div data-id="element-4709">
              <h3 className="text-sm text-gray-500" data-id="element-4710">Valid Identities</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4711">
                {campaign.validIdentities?.join(', ') || 'All identities'}
              </p>
            </div>
            <div data-id="element-4712">
              <h3 className="text-sm text-gray-500" data-id="element-4713">Code</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4714">
                {campaign.code}
              </p>
            </div>
            <div data-id="element-4715">
              <h3 className="text-sm text-gray-500" data-id="element-4716">Currency</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4717">LKR (Rs.)</p>
            </div>
            <div data-id="element-4718">
              <h3 className="text-sm text-gray-500" data-id="element-4719">Discount Percentage</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4720">
                {campaign.discountPercentage}%
              </p>
            </div>
            <div data-id="element-4721">
              <h3 className="text-sm text-gray-500" data-id="element-4722">Fixed discount amount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4723">
                Rs.{' '}
                {campaign.fixedDiscountAmount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-4724">
              <h3 className="text-sm text-gray-500" data-id="element-4725">Discount carried by</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4726">
                {campaign.discountCarriedBy}
              </p>
            </div>
            <div data-id="element-4727">
              <h3 className="text-sm text-gray-500" data-id="element-4728">
                Discount percentage carried by merchant
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4729">
                {campaign.merchantPercentage || 0}%
              </p>
            </div>
            <div data-id="element-4730">
              <h3 className="text-sm text-gray-500" data-id="element-4731">Budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4732">
                Rs. {campaign.budget.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-4733">
              <h3 className="text-sm text-gray-500" data-id="element-4734">Budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4735">
                Rs. {campaign.budgetUsed.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-4736">
              <h3 className="text-sm text-gray-500" data-id="element-4737">Recurring budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4738">
                {campaign.recurringBudget ? `Rs. ${campaign.recurringBudget.toLocaleString('en-LK')}` : 'None'}
              </p>
            </div>
            <div data-id="element-4739">
              <h3 className="text-sm text-gray-500" data-id="element-4740">Recurring budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4741">
                {campaign.recurringBudgetUsed ? `Rs. ${campaign.recurringBudgetUsed.toLocaleString('en-LK')}` : 'None'}
              </p>
            </div>
            <div data-id="element-4742">
              <h3 className="text-sm text-gray-500" data-id="element-4743">Recurring budget period</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4744">
                {campaign.recurringBudgetPeriod || 'No data available'}
              </p>
            </div>
            <div data-id="element-4745">
              <h3 className="text-sm text-gray-500" data-id="element-4746">
                Strictly enforce budget?
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4747">
                {campaign.strictlyEnforceBudget ? 'Yes' : 'No'}
              </p>
            </div>
            <div data-id="element-4748">
              <h3 className="text-sm text-gray-500" data-id="element-4749">Companies</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4750">
                {campaign.companies?.join(', ') || 'All companies'}
              </p>
            </div>
            <div data-id="element-4751">
              <h3 className="text-sm text-gray-500" data-id="element-4752">
                Limit usage per total customer
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-4753">
                {campaign.limitUsage || 'No information'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}